// ComponentTools — a component's tool inventory: capability-type count chips on
// top, then the tool table (capability score, type tags, status), expandable for
// the full description. This is where "what can this thing actually do" lives.
import { useMemo, useState, type Key } from "react";
import { Table, Typography, Input, theme } from "antd";
import type { TableColumnsType } from "antd";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { ToneTag, ScoreMeter, Th } from "./kit";
import {
  TOOL_KIND_ORDER,
  TOOL_KIND_HINT,
  toolKindTone,
  tierToTone,
  type VMToolRow,
} from "@/lib/api/scanReport";

const { Text, Paragraph } = Typography;

export default function ComponentTools({
  tools,
  typeCounts,
}: {
  tools: VMToolRow[];
  typeCounts: Record<string, number>;
}) {
  const { token } = theme.useToken();
  const [q, setQ] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const f = needle
      ? tools.filter(
          (t) =>
            t.tool_name.toLowerCase().includes(needle) ||
            (t.tool_description || "").toLowerCase().includes(needle),
        )
      : tools;
    // Riskiest first: lowest trust score at the top. Unscored tools are
    // UN-assessed, not safe, so they sort to the very top (−1), never below 100.
    return [...f].sort((a, b) => (a.score ?? -1) - (b.score ?? -1));
  }, [tools, q]);

  const columns: TableColumnsType<VMToolRow> = [
    {
      title: Th("Name"),
      key: "name",
      width: 220,
      sorter: (a, b) => a.tool_name.localeCompare(b.tool_name),
      render: (_, t) => (
        <Text className="text-text-primary" style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 12 }}>
          {t.tool_name}
        </Text>
      ),
    },
    {
      title: Th("Description"),
      key: "desc",
      render: (_, t) => (
        <Text className="text-sm text-text-secondary" ellipsis={{ tooltip: t.tool_description }}>
          {t.tool_description}
        </Text>
      ),
    },
    {
      title: Th("Trust"),
      key: "score",
      width: 132,
      sorter: (a, b) => (a.score ?? -1) - (b.score ?? -1),
      render: (_, t) => <ScoreMeter score={t.score} tone={tierToTone(t.tier ?? undefined)} />,
    },
    {
      title: Th("Capabilities"),
      key: "type",
      width: 240,
      render: (_, t) => (
        <div className="flex flex-wrap gap-1">
          {t.kinds.map((k) => (
            <ToneTag key={k} tone={toolKindTone(k)} title={TOOL_KIND_HINT[k]}>
              {k}
            </ToneTag>
          ))}
        </div>
      ),
    },
    {
      title: Th("Status"),
      key: "status",
      width: 130,
      render: (_, t) => (
        <ToneTag tone={t.status === "IMPLEMENTED" ? "info" : "muted"}>{t.status.toLowerCase().replace("_", " ")}</ToneTag>
      ),
    },
  ];

  return (
    <div>
      {/* capability-type count chips */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-baseline gap-1.5">
          <span className="font-semibold tabular-nums text-text-primary">{tools.length}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">total</span>
        </span>
        {TOOL_KIND_ORDER.map(({ key, label, countKey, hint }) => {
          const n = typeCounts[countKey] ?? 0;
          if (!n) return null;
          return (
            <ToneTag key={key} tone={toolKindTone(key)} title={hint}>
              {n} {label}
            </ToneTag>
          );
        })}
        <div className="ml-auto">
          <Input
            size="small"
            allowClear
            prefix={<Search size={13} style={{ color: token.colorTextTertiary }} />}
            placeholder="search name / description"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 220 }}
          />
        </div>
      </div>
      <Table<VMToolRow>
        size="small"
        rowKey={(t) => t.tool_name}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 760 }}
        pagination={
          rows.length > 20
            ? {
                defaultPageSize: 50,
                pageSizeOptions: [20, 50, 100, 200],
                showSizeChanger: true,
                size: "small",
                showTotal: (total, range) => (
                  <span className="text-[11px] uppercase tracking-[0.5px] text-text-muted">
                    {range[0]}–{range[1]} of {total}
                  </span>
                ),
              }
            : false
        }
        locale={{ emptyText: "No tools recorded." }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as Key[]),
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand, record }) => (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse tool detail" : "Expand tool detail"}
              onClick={(e) => onExpand(record, e)}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                background: "transparent",
                border: "none",
                padding: 0,
                color: token.colorTextTertiary,
              }}
            >
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          ),
          expandedRowRender: (t) => (
            <div className="px-2 py-2">
              <Paragraph className="!mb-2 text-sm text-text-secondary">{t.tool_description}</Paragraph>
              <div className="flex flex-wrap gap-2 text-xs text-text-muted">
                {t.band && <span>capability band: {t.band}</span>}
                {t.tier && <span>· tier: {t.tier}</span>}
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
}
